import {BasketToken} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_token.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useIsLastNavigationHistoryBack} from '@aboutyou/router';
import {useBasketToken} from '../basketStore/BasketTokenProvider';

/**
 * This hook returns the stale basket token used in the stream page when user navigates back from any other page which can update the global basket token.
 * Example: User scrolling on a category pages goes to an ADP and Add to Basket, then navigates back to the category page.
 *
 * Using the stale basket token in this case:
 * - Prevents showing the skeletons on the category page since we dont have have data for the stream+new basket token yet.
 * - Prevents the stream from firing many requests to the backend for all the pages user has scrolled through.
 *
 * Caveats:
 * - Since we use a stale basket token on navigation back, if we have any features in the product tile which depends on the basket token will not work correctly.
 * - Currently, we pass the basket token to the BE only for getting coupons in the product tile, this will not be affected if we pass a slightly stale basket token.
 * - `In Basket` badge functionality will not be affected since it doesn't depend on we passing the basketToken to the BE.
 *
 * - This implementation is in-line with how the App also works, where navigation back doesn't update the stream data.
 */
export const useNavigationBackOptimizedBasketToken = ({
  uniqueKey,
}: {
  uniqueKey: string;
}) => {
  const {token: freshToken} = useBasketToken();
  const [rememberedBasketToken] = useHistoryPageState<BasketToken | null>(
    freshToken,
    {uniqueKey: `maybe_stale_basket_token::${uniqueKey}`},
  );

  const isLastNavigationHistoryBack = useIsLastNavigationHistoryBack();

  return {
    basketToken: isLastNavigationHistoryBack
      ? rememberedBasketToken
      : freshToken,
  };
};
